import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  LayoutDashboard, LogOut, Clock, Calendar, Users, 
  Search, Plus, Download, Building, Bot, Repeat, Pause, Play, Trash2, Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  PaymentModal, 
  ShirtsModal, 
  PrepaidModal, 
  TransactionHistoryModal, 
  RecurringModal, 
  AddCustomerModal,
  PaymentLinksModal,
  RecurringList,
  CustomerCard,
  LoyaltySection,
  EditLoyaltyModal,
  CustomerListItem
} from "@/components/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminCustomers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const filterPro = searchParams.get("filter") === "pro";
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]); // Store all customers for local filtering
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const debounceRef = useRef(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [excludedAmount, setExcludedAmount] = useState(""); // Montant hors fidélité (robes de mariée)
  const [discountApplied, setDiscountApplied] = useState("");
  const [editLoyaltyCustomer, setEditLoyaltyCustomer] = useState(null);
  const [newLoyaltyBalance, setNewLoyaltyBalance] = useState("");
  const [shirtsCustomer, setShirtsCustomer] = useState(null);
  const [shirtsAction, setShirtsAction] = useState("add");
  const [shirtsQuantity, setShirtsQuantity] = useState("");
  const [prepaidCustomer, setPrepaidCustomer] = useState(null);
  const [prepaidAction, setPrepaidAction] = useState("add");
  const [prepaidAmount, setPrepaidAmount] = useState("");
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recurringSubscriptions, setRecurringSubscriptions] = useState([]);
  const [showRecurringSection, setShowRecurringSection] = useState(false);
  const [recurringCustomer, setRecurringCustomer] = useState(null);
  const [recurringDay, setRecurringDay] = useState("");
  const [recurringTimeSlot, setRecurringTimeSlot] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("weekly");
  const [recurringZoneConfig, setRecurringZoneConfig] = useState(null);
  const [paymentLinksCustomer, setPaymentLinksCustomer] = useState(null);
  const [treatmentsCustomer, setTreatmentsCustomer] = useState(null);
  const [filterAccountCreated, setFilterAccountCreated] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    first_name: "",
    last_name: "",
    address: "",
    phone: "",
    email: "",
    notes: ""
  });

  const getAuth = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  };

  const fetchRecurringSubscriptions = async () => {
    const headers = getAuth();
    if (!headers) return;
    try {
      const response = await axios.get(`${API}/admin/recurring-subscriptions`, { headers });
      setRecurringSubscriptions(response.data || []);
    } catch (error) {
      console.error("Error fetching recurring subscriptions:", error);
    }
  };

  const fetchCustomers = useCallback(async (preserveFilter = false, currentQuery = "") => {
    const headers = getAuth();
    if (!headers) return;
    
    try {
      // Always fetch ALL customers (no search param)
      const response = await axios.get(`${API}/admin/customers`, { headers });
      setAllCustomers(response.data);
      
      // If preserveFilter is true and there's an active search, reapply the filter
      if (preserveFilter && currentQuery.trim()) {
        const lowerQuery = currentQuery.toLowerCase();
        const filtered = response.data.filter(c => 
          (c.first_name || "").toLowerCase().includes(lowerQuery) ||
          (c.last_name || "").toLowerCase().includes(lowerQuery) ||
          (c.email || "").toLowerCase().includes(lowerQuery) ||
          (c.phone || "").toLowerCase().includes(lowerQuery) ||
          (c.address || "").toLowerCase().includes(lowerQuery) ||
          (`${c.first_name || ""} ${c.last_name || ""}`).toLowerCase().includes(lowerQuery)
        );
        setCustomers(filtered);
      } else {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      if (error.response?.status === 401) {
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCustomers(false, "");
    fetchRecurringSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to refresh data while preserving search filter
  const refreshCustomers = () => {
    fetchCustomers(true, searchQuery);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Just apply filter locally, no need to refetch
    if (!searchQuery.trim()) {
      setCustomers(allCustomers);
    } else {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = allCustomers.filter(c => 
        (c.first_name || "").toLowerCase().includes(lowerQuery) ||
        (c.last_name || "").toLowerCase().includes(lowerQuery) ||
        (c.email || "").toLowerCase().includes(lowerQuery) ||
        (c.phone || "").toLowerCase().includes(lowerQuery) ||
        (c.address || "").toLowerCase().includes(lowerQuery) ||
        (`${c.first_name || ""} ${c.last_name || ""}`).toLowerCase().includes(lowerQuery)
      );
      setCustomers(filtered);
    }
  };

  // Local filtering when typing (instant feedback) - NO server fetch
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Instant local filtering only
    if (!query.trim()) {
      setCustomers(allCustomers);
    } else {
      const lowerQuery = query.toLowerCase();
      const filtered = allCustomers.filter(c => 
        (c.first_name || "").toLowerCase().includes(lowerQuery) ||
        (c.last_name || "").toLowerCase().includes(lowerQuery) ||
        (c.email || "").toLowerCase().includes(lowerQuery) ||
        (c.phone || "").toLowerCase().includes(lowerQuery) ||
        (c.address || "").toLowerCase().includes(lowerQuery) ||
        (`${c.first_name || ""} ${c.last_name || ""}`).toLowerCase().includes(lowerQuery)
      );
      setCustomers(filtered);
    }
  };

  const resetForm = () => {
    setCustomerForm({
      first_name: "",
      last_name: "",
      address: "",
      phone: "",
      email: "",
      notes: ""
    });
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.post(`${API}/admin/customers`, customerForm, { headers });
      toast.success("Client ajouté");
      setShowAddDialog(false);
      resetForm();
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const startEdit = (customer) => {
    setEditingCustomer(customer.id);
    setCustomerForm({
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      address: customer.address || "",
      phone: customer.phone || "",
      email: customer.email || "",
      notes: customer.notes || ""
    });
  };

  const cancelEdit = () => {
    setEditingCustomer(null);
    resetForm();
  };

  const handleUpdateCustomer = async (customerId) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.put(`${API}/admin/customers/${customerId}`, customerForm, { headers });
      toast.success("Client mis à jour");
      setEditingCustomer(null);
      resetForm();
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/customers/${customerId}`, { headers });
      toast.success("Client supprimé");
      refreshCustomers();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const [importing, setImporting] = useState(false);
  
  const handleImportFromBookings = async () => {
    const headers = getAuth();
    if (!headers) return;

    setImporting(true);
    try {
      const response = await axios.post(`${API}/admin/customers/import-from-bookings`, {}, { headers });
      toast.success(response.data.message);
      refreshCustomers();
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  // Payment functions
  const handleAddPayment = async () => {
    if (!paymentCustomer || !paymentAmount) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const discount = discountApplied ? parseFloat(discountApplied) : 0;
      const excluded = excludedAmount ? parseFloat(excludedAmount) : 0;
      const response = await axios.post(
        `${API}/admin/customers/${paymentCustomer.id}/payment`,
        { 
          amount: parseFloat(paymentAmount),
          discount_applied: discount,
          excluded_from_loyalty: excluded
        },
        { headers }
      );
      
      if (response.data.new_rewards > 0) {
        toast.success(`🎉 ${paymentAmount}€ enregistré ! Le client a gagné ${response.data.new_rewards} remise(s) de 5€ !`);
      } else {
        toast.success(response.data.message);
      }
      
      setPaymentCustomer(null);
      setPaymentAmount("");
      setDiscountApplied("");
      setExcludedAmount("");
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleUseReward = async (customer) => {
    if (!window.confirm(`Utiliser une récompense de 5€ pour ${customer.first_name} ${customer.last_name} ?`)) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/use-reward`,
        {},
        { headers }
      );
      toast.success(`5€ de réduction appliqués ! Reste: ${response.data.rewards_remaining} récompense(s)`);
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Toggle VIP status
  const handleToggleVip = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.put(
        `${API}/admin/customers/${customer.id}/vip`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Toggle PRO status
  const handleTogglePro = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/toggle-pro`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };


  // Recurring subscription management
  const handleToggleRecurring = async (subscriptionId) => {
    const headers = getAuth();
    if (!headers) return;
    try {
      const response = await axios.put(
        `${API}/admin/recurring-subscriptions/${subscriptionId}/toggle`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteRecurring = async (subscriptionId) => {
    if (!window.confirm("Supprimer cet abonnement ? Les réservations futures seront annulées.")) return;
    const headers = getAuth();
    if (!headers) return;
    try {
      const response = await axios.delete(
        `${API}/admin/recurring-subscriptions/${subscriptionId}`,
        { headers }
      );
      toast.success(response.data.message);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleCreateRecurringForCustomer = async () => {
    if (!recurringCustomer || recurringDay === "" || !recurringTimeSlot) {
      toast.error("Sélectionne un jour et un créneau horaire");
      return;
    }
    const headers = getAuth();
    if (!headers) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/recurring-subscriptions`,
        {
          zone_id: recurringCustomer.zone_id || "lyon",
          day_of_week: parseInt(recurringDay),
          time_slot: recurringTimeSlot,
          frequency: recurringFrequency,
          first_name: recurringCustomer.first_name,
          last_name: recurringCustomer.last_name,
          address: recurringCustomer.address || "",
          phone: recurringCustomer.phone || "",
          email: recurringCustomer.email
        },
        { headers }
      );
      toast.success(response.data.message);
      setRecurringCustomer(null);
      setRecurringDay("");
      setRecurringTimeSlot("");
      setRecurringFrequency("weekly");
      setRecurringZoneConfig(null);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const fetchZoneConfigForRecurring = async (zoneId) => {
    try {
      const response = await axios.get(`${API}/zone-config/${zoneId}`);
      setRecurringZoneConfig(response.data);
    } catch (error) {
      console.error("Error fetching zone config:", error);
      setRecurringZoneConfig(null);
    }
  };

  const closeRecurringDialog = () => {
    setRecurringCustomer(null);
    setRecurringDay("");
    setRecurringTimeSlot("");
    setRecurringFrequency("weekly");
    setRecurringZoneConfig(null);
  };

  const handleRecurringZoneChange = (v) => {
    setRecurringCustomer({...recurringCustomer, zone_id: v});
    setRecurringDay("");
    setRecurringTimeSlot("");
    fetchZoneConfigForRecurring(v);
  };

  // Edit loyalty balance
  const handleEditLoyalty = async () => {
    if (!editLoyaltyCustomer || newLoyaltyBalance === "") return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.put(
        `${API}/admin/customers/${editLoyaltyCustomer.id}/loyalty`,
        { loyalty_balance: parseFloat(newLoyaltyBalance) },
        { headers }
      );
      toast.success(`Progression mise à jour : ${newLoyaltyBalance}€`);
      setEditLoyaltyCustomer(null);
      setNewLoyaltyBalance("");
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  // Cleanup duplicate customers
  const handleCleanupDuplicates = async () => {
    if (!window.confirm("Supprimer tous les clients en doublon ? (garde uniquement le premier de chaque nom)")) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.delete(`${API}/admin/customers/cleanup-duplicates`, { headers });
      toast.success(response.data.message);
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du nettoyage");
    }
  };

  // Handle prepaid shirts
  const handleUpdateShirts = async () => {
    if (!shirtsCustomer || !shirtsQuantity) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.put(
        `${API}/admin/customers/${shirtsCustomer.id}/shirts`,
        { 
          action: shirtsAction,
          quantity: parseInt(shirtsQuantity)
        },
        { headers }
      );
      toast.success(response.data.message);
      setShirtsCustomer(null);
      setShirtsQuantity("");
      setShirtsAction("add");
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  // Handle prepaid balance
  const handleUpdatePrepaid = async () => {
    if (!prepaidCustomer || !prepaidAmount) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.put(
        `${API}/admin/customers/${prepaidCustomer.id}/prepaid`,
        { 
          action: prepaidAction,
          amount: parseFloat(prepaidAmount)
        },
        { headers }
      );
      toast.success(response.data.message);
      setPrepaidCustomer(null);
      setPrepaidAmount("");
      setPrepaidAction("add");
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  // Fetch transaction history
  const handleShowHistory = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.get(
        `${API}/admin/customers/${customer.id}/transactions`,
        { headers }
      );
      setTransactions(response.data);
      setHistoryCustomer(customer);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  // Navigate to booking with pre-filled customer
  const handleBookCustomer = (customer) => {
    // Store customer data in sessionStorage for the booking page
    sessionStorage.setItem("prefilledCustomer", JSON.stringify(customer));
    navigate("/admin/booking/new");
  };

  // Deduct free treatments
  const handleDeductTreatments = async () => {
    if (!treatmentsCustomer) return;
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${treatmentsCustomer.id}/deduct-treatments`,
        { amount: 1 },
        { headers }
      );
      
      toast.success(response.data.message);
      
      // Update local state
      setCustomers(prev =>
        prev.map(c =>
          c.id === treatmentsCustomer.id
            ? { ...c, free_treatments: response.data.remaining }
            : c
        )
      );
      setTreatmentsCustomer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la déduction");
    }
  };

  // Grant Sakura Tree bonus draw
  const handleGrantSakuraDraw = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/grant-sakura-draw`,
        {},
        { headers }
      );
      
      toast.success(response.data.message);
      
      // Update local state
      setCustomers(prev =>
        prev.map(c =>
          c.id === customer.id
            ? { ...c, sakura_bonus_draws: (c.sakura_bonus_draws || 0) + 1 }
            : c
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'attribution");
    }
  };

  // Remove Sakura Tree bonus draw (admin correction - NOT visible in customer history)
  const handleRemoveSakuraDraw = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    if (!window.confirm(`Retirer 1 tirage bonus de ${customer.first_name} ${customer.last_name} ?\n\nCette action ne sera PAS visible dans l'historique du client.`)) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/remove-sakura-draw`,
        {},
        { headers }
      );
      
      toast.success(response.data.message);
      
      // Update local state
      setCustomers(prev =>
        prev.map(c =>
          c.id === customer.id
            ? { ...c, sakura_bonus_draws: Math.max(0, (c.sakura_bonus_draws || 0) - 1) }
            : c
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du retrait");
    }
  };

  // Fill Sakura monthly gauge to 30€
  const handleFillSakuraGauge = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/fill-sakura-gauge`,
        {},
        { headers }
      );
      
      toast.success(response.data.message);
      
      // Update local state - set monthly_spending to 30
      setCustomers(prev =>
        prev.map(c =>
          c.id === customer.id
            ? { ...c, monthly_spending: 30 }
            : c
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du remplissage de la jauge");
    }
  };

  // Send SMS: Articles are ready
  const handleSendArticlesReadySms = async (customer) => {
    const headers = getAuth();
    if (!headers) return;

    if (!customer.phone) {
      toast.error("Ce client n'a pas de numéro de téléphone");
      return;
    }

    if (!window.confirm(`Envoyer un SMS à ${customer.first_name} ${customer.last_name} pour lui dire que ses articles sont prêts ?`)) return;

    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/sms-articles-ready`,
        {},
        { headers }
      );
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du SMS");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar hidden md:block">
        <div className="flex items-center justify-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-16 w-auto bg-white rounded-lg p-1"
          />
        </div>
        <nav className="space-y-2">
          <div className="admin-nav-item" onClick={() => navigate("/admin/dashboard")} data-testid="nav-dashboard">
            <LayoutDashboard className="w-5 h-5" />
            <span>Tableau de bord</span>
          </div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/planning")} data-testid="nav-planning">
            <Calendar className="w-5 h-5" />
            <span>Planning</span>
          </div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/slots")} data-testid="nav-slots">
            <Clock className="w-5 h-5" />
            <span>Créneaux</span>
          </div>
          <div className="admin-nav-item active" data-testid="nav-customers">
            <Users className="w-5 h-5" />
            <span>Clients</span>
          </div>
        </nav>
        <div className="absolute bottom-6 left-6">
          <button onClick={handleLogout} className="admin-nav-item text-slate-400 hover:text-white" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-auto pb-24 md:pb-10">
        {/* Mobile Navigation */}
        <div className="md:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Clients
            </h1>
            <button onClick={handleLogout} className="p-2 text-slate-500">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/dashboard")} className="flex-shrink-0">
              <LayoutDashboard className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/planning")} className="flex-shrink-0">
              <Calendar className="w-4 h-4 mr-1" />
              Planning
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/slots")} className="flex-shrink-0">
              <Clock className="w-4 h-4 mr-1" />
              Créneaux
            </Button>
            <Button size="sm" className="bg-primary text-white flex-shrink-0">
              <Users className="w-4 h-4 mr-1" />
              Clients
            </Button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="hidden md:flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                {filterPro ? (
                  <>
                    <Building className="w-6 h-6 text-blue-600" />
                    Clients Professionnels
                  </>
                ) : (
                  "Gestion des clients"
                )}
              </h1>
              <p className="text-slate-500 mt-1">
                {filterPro ? "Clients avec statut PRO" : "Retrouvez et gérez vos clients"}
              </p>
            </div>
            <div className="flex gap-2">
              {filterPro && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/admin/customers")}
                >
                  Voir tous les clients
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={handleImportFromBookings}
                disabled={importing}
              >
                <Download className="w-4 h-4 mr-2" />
                {importing ? "Import..." : "Importer depuis réservations"}
              </Button>
              <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un client
              </Button>
            </div>
          </div>

          {/* Mobile Buttons */}
          <div className="md:hidden mb-4 space-y-2">
            <Button 
              variant="outline" 
              onClick={handleImportFromBookings}
              disabled={importing}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Import en cours..." : "Importer depuis réservations"}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleCleanupDuplicates}
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer les doublons
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="w-full bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un client
            </Button>
          </div>

          {/* Search Bar */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par nom, email, téléphone ou adresse..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
                <Button type="submit">Rechercher</Button>
              </form>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${!filterAccountCreated ? 'ring-2 ring-pink-500' : ''}`}
              onClick={() => setFilterAccountCreated(false)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total clients</p>
                    <p className="text-2xl font-bold">{allCustomers.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${filterAccountCreated ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setFilterAccountCreated(true)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Avec compte</p>
                    <p className="text-2xl font-bold">{allCustomers.filter(c => c.password_hash || c.google_id).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recurring Subscriptions Section */}
          <RecurringList
            subscriptions={recurringSubscriptions}
            showSection={showRecurringSection}
            onToggleSection={() => setShowRecurringSection(!showRecurringSection)}
            onToggleRecurring={handleToggleRecurring}
            onDeleteRecurring={handleDeleteRecurring}
          />

          {/* Customers List */}
          <Card>
            <CardHeader>
              <CardTitle>
                {filterPro ? "Clients Professionnels" : filterAccountCreated ? "Clients avec compte" : "Liste des clients"}
                {filterAccountCreated && (
                  <span className="ml-2 text-sm font-normal text-green-600">(inscrits via l'app)</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : (() => {
                let filteredList = customers;
                if (filterPro) {
                  filteredList = customers.filter(c => c.is_pro);
                } else if (filterAccountCreated) {
                  filteredList = customers.filter(c => c.password_hash || c.google_id);
                }
                return filteredList.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    {filterPro ? "Aucun client PRO" : filterAccountCreated ? "Aucun client avec compte" : (searchQuery ? "Aucun client trouvé" : "Aucun client enregistré")}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredList.map((customer) => (
                      <CustomerListItem
                        key={customer.id}
                        customer={customer}
                        isEditing={editingCustomer === customer.id}
                        editForm={customerForm}
                        setEditForm={setCustomerForm}
                        onStartEdit={startEdit}
                        onSaveEdit={() => handleUpdateCustomer(customer.id)}
                        onCancelEdit={cancelEdit}
                        onDelete={handleDeleteCustomer}
                        onToggleVip={handleToggleVip}
                        onTogglePro={handleTogglePro}
                      onEditLoyalty={(c) => {
                        setEditLoyaltyCustomer(c);
                        setNewLoyaltyBalance((c.loyalty_balance || 0).toString());
                      }}
                      onManageShirts={setShirtsCustomer}
                      onManagePrepaid={setPrepaidCustomer}
                      onManageTreatments={setTreatmentsCustomer}
                      onGrantSakuraDraw={handleGrantSakuraDraw}
                      onRemoveSakuraDraw={handleRemoveSakuraDraw}
                      onFillSakuraGauge={handleFillSakuraGauge}
                      onAddPayment={setPaymentCustomer}
                      onShowHistory={handleShowHistory}
                      onPaymentLinks={setPaymentLinksCustomer}
                      onCreateRecurring={(c) => { setRecurringCustomer(c); setRecurringDay(""); }}
                      onBookCustomer={handleBookCustomer}
                      onSendArticlesReadySms={handleSendArticlesReadySms}
                    />
                  ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Payment Dialog */}
        <PaymentModal
          customer={paymentCustomer}
          paymentAmount={paymentAmount}
          setPaymentAmount={setPaymentAmount}
          discountApplied={discountApplied}
          setDiscountApplied={setDiscountApplied}
          excludedAmount={excludedAmount}
          setExcludedAmount={setExcludedAmount}
          onSubmit={handleAddPayment}
          onClose={() => setPaymentCustomer(null)}
        />

        {/* Edit Loyalty Balance Dialog */}
        <EditLoyaltyModal
          customer={editLoyaltyCustomer}
          value={newLoyaltyBalance}
          onChange={setNewLoyaltyBalance}
          onSave={handleEditLoyalty}
          onClose={() => { setEditLoyaltyCustomer(null); setNewLoyaltyBalance(""); }}
        />

        {/* Prepaid Shirts Dialog */}
        <ShirtsModal
          customer={shirtsCustomer}
          action={shirtsAction}
          setAction={setShirtsAction}
          quantity={shirtsQuantity}
          setQuantity={setShirtsQuantity}
          onSubmit={handleUpdateShirts}
          onClose={() => setShirtsCustomer(null)}
        />

        {/* Prepaid Balance Dialog */}
        <PrepaidModal
          customer={prepaidCustomer}
          action={prepaidAction}
          setAction={setPrepaidAction}
          amount={prepaidAmount}
          setAmount={setPrepaidAmount}
          onSubmit={handleUpdatePrepaid}
          onClose={() => setPrepaidCustomer(null)}
        />

        {/* Transaction History Dialog */}
        <TransactionHistoryModal
          customer={historyCustomer}
          transactions={transactions}
          onClose={() => { setHistoryCustomer(null); setTransactions([]); }}
        />

        {/* Create Recurring Dialog */}
        <RecurringModal
          customer={recurringCustomer}
          day={recurringDay}
          setDay={setRecurringDay}
          timeSlot={recurringTimeSlot}
          setTimeSlot={setRecurringTimeSlot}
          frequency={recurringFrequency}
          setFrequency={setRecurringFrequency}
          onZoneChange={handleRecurringZoneChange}
          onSubmit={handleCreateRecurringForCustomer}
          onClose={closeRecurringDialog}
        />

        {/* Add Customer Dialog */}
        <AddCustomerModal
          show={showAddDialog}
          form={customerForm}
          setForm={setCustomerForm}
          onSubmit={handleAddCustomer}
          onClose={() => { setShowAddDialog(false); resetForm(); }}
        />

        {/* Payment Links Dialog */}
        {paymentLinksCustomer && (
          <PaymentLinksModal
            customer={paymentLinksCustomer}
            onClose={() => setPaymentLinksCustomer(null)}
            getAuth={getAuth}
          />
        )}

        {/* Deduct Treatments Dialog */}
        {treatmentsCustomer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                🧴 Déduire un traitement offert
              </h3>
              <p className="text-slate-600 mb-4">
                <strong>{treatmentsCustomer.first_name} {treatmentsCustomer.last_name}</strong> a actuellement <strong className="text-purple-600">{treatmentsCustomer.free_treatments || 0} traitement(s)</strong> offert(s).
              </p>
              {(treatmentsCustomer.free_treatments || 0) > 0 ? (
                <>
                  <p className="text-sm text-slate-500 mb-4">
                    Voulez-vous déduire 1 traitement additionnel pour cette commande ?
                  </p>
                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setTreatmentsCustomer(null)}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleDeductTreatments}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Déduire 1 traitement
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
                    Ce client n'a aucun traitement offert disponible.
                  </p>
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setTreatmentsCustomer(null)}>
                      Fermer
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminCustomers;
